import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import {
  SerifHeading,
  HPrimaryButton,
  HSecondaryButton,
  HDestructiveButton,
  HButtonPair,
} from '../h';

/* v9 5.3 — Higlo rename, inline.

   Renders the Higlo name as a big serif heading by default. Clicking the
   heading (or pressing the external "Rename" ghost button) flips it into a
   text input with Cancel / Save buttons beneath.

   Two modes:
     mode='account'    — the Higlo-detail rename. Fans the new name out to
                         every owned family in this account that is a
                         member of the Higlo. Pass `targetFamilyIDs`.
                         A smart-check first looks at every other target's
                         current label — if any differ from both the
                         current displayed name AND the proposed new name,
                         a warning lists the conflicts and the Save button
                         turns into a destructive "Replace all names".
     mode='thisHearth' — the per-Hearth rename on the position page. Writes
                         only the single `editFamilyID` doc. Other Hearths'
                         labels are unaffected. No smart-check.

   Writes always go to `familyInfo/{fid}/igloos/{iglooID}.name`. There is
   no global iglooInfo.name surface to update.

   Props:
     iglooID              — required.
     editFamilyID         — the family whose "igloos/{iglooID}" doc shows
                            the current name in the heading. In 'account'
                            mode this is the primary; in 'thisHearth' mode
                            it's the Hearth being configured.
     mode                 — 'account' | 'thisHearth' (default 'account').
     targetFamilyIDs      — required when mode='account'. The list of
                            account families to fan the rename out to.
                            Only docs that already exist are written.
     currentName          — value shown in the input.
     subtitle             — small 12px muted line below the heading (e.g.
                            "Private family space · 2 Hearths").
     editing, onBegin,    — controlled state so the parent can wire this up
     onCancel               to its own "Rename" button.
     onSaved(name)        — called after a successful write.
*/
export function HigloRename({
  iglooID,
  editFamilyID,
  mode = 'account',
  targetFamilyIDs,
  currentName,
  subtitle,
  editing,
  onBegin,
  onCancel,
  onSaved,
}) {
  const [value, setValue] = useState(currentName || '');
  const [saving, setSaving] = useState(false);
  // null when no conflict; otherwise [{ familyID, familyName, currentLabel }]
  const [conflictPreview, setConflictPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setValue(currentName || '');
      setConflictPreview(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, currentName]);

  const doCommit = async (trimmed) => {
    setSaving(true);
    try {
      if (mode === 'account') {
        const targets = (targetFamilyIDs && targetFamilyIDs.length > 0)
          ? targetFamilyIDs
          : [editFamilyID];
        const batch = writeBatch(db);
        for (const fid of targets) {
          batch.update(doc(db, 'familyInfo', fid, 'igloos', iglooID), {
            name: trimmed,
          });
        }
        await batch.commit();
      } else {
        await updateDoc(doc(db, 'familyInfo', editFamilyID, 'igloos', iglooID), {
          name: trimmed,
        });
      }
      onSaved?.(trimmed);
    } catch (err) {
      console.error('HigloRename save error', err);
    } finally {
      setSaving(false);
    }
  };

  const attemptCommit = async () => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      onCancel?.();
      return;
    }
    if (!iglooID || !editFamilyID) {
      onCancel?.();
      return;
    }
    const isUnchanged = trimmed === currentName;
    if (mode !== 'account') {
      // Per-Hearth rename: no smart-check, no unify scenario.
      if (isUnchanged) onCancel?.();
      else await doCommit(trimmed);
      return;
    }
    const targets = (targetFamilyIDs && targetFamilyIDs.length > 0)
      ? targetFamilyIDs
      : [editFamilyID];
    if (targets.length <= 1) {
      // Single-family in this Higlo — no other perspective to conflict with.
      if (isUnchanged) onCancel?.();
      else await doCommit(trimmed);
      return;
    }
    setSaving(true);
    try {
      // Smart check: read each target family's igloos/{iglooID}.name. A
      // conflict is a label that differs from both the current displayed
      // name AND the proposed new name — i.e. a per-perspective custom
      // name we'd be silently overwriting.
      // We run this even when the value is unchanged from the current
      // displayed name so the user can hit Save without editing to unify a
      // stale per-perspective label across the account.
      const conflicts = [];
      await Promise.all(
        targets.map(async (fid) => {
          if (fid === editFamilyID) return;
          try {
            const snap = await getDoc(
              doc(db, 'familyInfo', fid, 'igloos', iglooID),
            );
            const existing = snap.exists() ? snap.data().name || null : null;
            if (
              existing
              && existing !== currentName
              && existing !== trimmed
            ) {
              const familySnap = await getDoc(doc(db, 'familyInfo', fid));
              const familyName = familySnap.exists()
                ? familySnap.data().name || 'a Hearth'
                : 'a Hearth';
              conflicts.push({ familyID: fid, familyName, currentLabel: existing });
            }
          } catch (err) {
            console.warn('HigloRename conflict check failed', fid, err.code);
          }
        }),
      );
      if (conflicts.length === 0) {
        if (isUnchanged) {
          onCancel?.();
          setSaving(false);
        } else {
          await doCommit(trimmed);
        }
      } else {
        setConflictPreview(conflicts);
        setSaving(false);
      }
    } catch (err) {
      console.error('HigloRename attemptCommit error', err);
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={onBegin}
        className="text-left w-full group"
        aria-label="Rename this Higlo"
      >
        <SerifHeading size="xl" className="group-hover:underline decoration-slate-200 underline-offset-4">
          {currentName || 'Higlo'}
        </SerifHeading>
        {subtitle && (
          <div className="text-[12px] text-slate-500 mt-1">{subtitle}</div>
        )}
      </button>
    );
  }

  const trimmedValue = (value || '').trim();

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !conflictPreview) attemptCommit();
          if (e.key === 'Enter' && conflictPreview) doCommit(trimmedValue);
          if (e.key === 'Escape') onCancel?.();
        }}
        maxLength={40}
        disabled={saving}
        className="w-full text-[20px] font-medium text-slate-900 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-slate-900"
      />
      <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
        {mode === 'thisHearth'
          ? 'Up to 40 characters. Used only on this Hearth.'
          : 'Up to 40 characters. Used on every Hearth in your account that’s in this Higlo.'}
      </p>
      {conflictPreview && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-[12px] text-amber-900 font-medium leading-relaxed mb-1">
            Some of your other Hearths know this Higlo by a different name:
          </p>
          <ul className="text-[12px] text-amber-900 leading-relaxed list-disc pl-5 mb-2">
            {conflictPreview.map((c) => (
              <li key={c.familyID}>
                <strong>{c.familyName}</strong> calls this Higlo, {c.currentLabel}
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-amber-900 leading-relaxed">
            Renaming will replace those names with &ldquo;{trimmedValue}&rdquo;.
          </p>
        </div>
      )}
      <HButtonPair className="mt-3">
        <HSecondaryButton onClick={onCancel} disabled={saving}>
          Cancel
        </HSecondaryButton>
        {conflictPreview ? (
          <HDestructiveButton onClick={() => doCommit(trimmedValue)} disabled={saving}>
            {saving ? 'Saving…' : 'Replace all names'}
          </HDestructiveButton>
        ) : (
          <HPrimaryButton onClick={attemptCommit} disabled={saving}>
            {saving ? 'Saving…' : 'Save name'}
          </HPrimaryButton>
        )}
      </HButtonPair>
    </div>
  );
}
